import React, { useRef, useState, useEffect, useCallback } from "react";
import { IoClose } from "react-icons/io5";
import { TbArrowsMinimize } from "react-icons/tb";
import { FiTrash2 } from "react-icons/fi";
import axios from "axios";
import { toast } from "react-toastify";
import { convertToDisplayUnit } from "../../../utils/quantityConversion";

const InventoryItemDetailModal = ({
    showModal,
    setShowModal,
    itemDetails,
    selectedOutlet,
    baseUrl,
    token,
}) => {
    const modalRef = useRef(null);
    const dragStartRef = useRef(null);
    const [offset, setOffset] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    // Confirmation state
    const [confirmRow, setConfirmRow] = useState(null); // detail object pending delete
    const [deleting, setDeleting] = useState(false);

    const summary = itemDetails?.consumption_details?.summary;

    useEffect(() => {
        if (showModal) setOffset(null);
    }, [showModal]);

    // ── drag logic ──────────────────────────────────────────────────────────
    const handleMouseDown = useCallback((e) => {
        if (e.target.closest("button")) return;
        e.preventDefault();
        const el = modalRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            elX: rect.left,
            elY: rect.top,
        };
        setIsDragging(true);
    }, []);

    const handleMouseMove = useCallback((e) => {
        if (!dragStartRef.current) return;
        const { mouseX, mouseY, elX, elY } = dragStartRef.current;
        setOffset({ x: elX + (e.clientX - mouseX), y: elY + (e.clientY - mouseY) });
    }, []);

    const handleMouseUp = useCallback(() => {
        dragStartRef.current = null;
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener("mousemove", handleMouseMove);
            window.addEventListener("mouseup", handleMouseUp);
        }
        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // ── helpers ─────────────────────────────────────────────────────────────
    const getDisplayQuantity = (quantity) => {
        const unitDef = summary?.unitdefinition;
        if (!summary?.primary_unit || !summary?.secondary_unit) return quantity;
        return convertToDisplayUnit(quantity, unitDef?.uom, summary?.secondary_unit, unitDef?.unit);
    };

    const displayUom =
        summary?.primary_unit && summary?.secondary_unit ? summary.secondary_unit : "";

    // ── delete ───────────────────────────────────────────────────────────────
    const handleDeleteConfirm = async () => {
        if (!confirmRow) return;
        setDeleting(true);
        try {
            await axios.post(`${baseUrl}/recipe-item/bulk-delete-with-recalc`, {
                token,
                recipe_item_deletes: [
                    {
                        outlet: selectedOutlet ?? selectedOutlet,
                        recipe_name: confirmRow.recipe_item,
                        item_name: itemDetails?.item_name,
                    },
                ],
            });
            toast.success(`Deleted "${confirmRow.recipe_item}" successfully`);
            setConfirmRow(null);
            // Optimistically remove the row from the local details
            if (itemDetails?.consumption_details?.details) {
                itemDetails.consumption_details.details =
                    itemDetails.consumption_details.details.filter(
                        (d) => d.recipe_item !== confirmRow.recipe_item
                    );
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Delete failed");
        } finally {
            setDeleting(false);
        }
    };

    if (!showModal) return null;

    const modalStyle =
        offset !== null
            ? { position: "fixed", top: offset.y, left: offset.x, transform: "none" }
            : {};

    const recipeCount = itemDetails?.consumption_details?.details?.length ?? 0;

    return (
        <div
            className={`fixed inset-0 z-50 ${offset === null ? "flex items-center justify-center" : ""} bg-black/50 p-4`}
            style={{ pointerEvents: isDragging ? "none" : "auto" }}
        >
            <div
                ref={modalRef}
                style={{ ...modalStyle, pointerEvents: "auto" }}
                className="w-full max-w-6xl rounded-xl bg-white shadow-2xl max-h-[90vh] overflow-hidden flex flex-col select-none"
            >
                {/* ── Header ── */}
                <div
                    onMouseDown={handleMouseDown}
                    className={`flex items-center justify-between border-b px-4 py-3 ${isDragging ? "cursor-grabbing" : "cursor-grab"} bg-white rounded-t-xl`}
                >
                    <div className="flex gap-2 items-center">
                        <div className="text-lg font-semibold text-gray-800">
                            {itemDetails?.item_name}
                        </div>
                        <div className="text-sm font-semibold text-gray-700 mt-1">
                            (From {itemDetails?.start_date} → {itemDetails?.end_date})
                        </div>
                    </div>

                    <button
                        onClick={() => setShowModal(false)}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition"
                    >
                        <IoClose size={18} />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="overflow-y-auto px-4 py-3 tiny-scrollbar">

                    {/* Item Details */}
                    <div className="grid grid-cols-5 gap-4 mb-3 rounded-lg border bg-gray-50 px-4 py-3 font-semibold text-gray-800 text-sm">
                        <div>Group : {summary?.groupname}</div>
                        <div>Primary UOM : {summary?.primary_unit}</div>
                        <div>Secondary UOM : {summary?.secondary_unit}</div>
                        <div>Unit Size : {summary?.unitdefinition?.unit}</div>
                        <div>Base UOM : {summary?.unitdefinition?.uom}</div>
                    </div>

                    {/* Table */}
                    <div className="rounded-lg border overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                            <thead className="bg-gray-100">
                                <tr>
                                    {[
                                        "Recipe Item",
                                        "Item Type",
                                        "Recipe Usage",
                                        "Sub-Recipe Item",
                                        "Sub-Recipe Usage",
                                        "Total Sold",
                                        "Total Consumed",
                                        "",
                                    ].map((col, i) => (
                                        <th
                                            key={i}
                                            className="px-2 py-3 text-xs font-semibold uppercase tracking-wide text-gray-700"
                                        >
                                            {col}
                                        </th>
                                    ))}
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-100 bg-white">
                                {itemDetails?.consumption_details?.details?.length > 0 ? (
                                    itemDetails.consumption_details.details.map((detail, idx) => (
                                        <tr
                                            key={idx}
                                            className="hover:bg-gray-50 cursor-pointer"
                                            onClick={() => {
                                                localStorage.setItem("group", summary?.groupname);
                                                localStorage.setItem("item", detail.recipe_item);
                                                window.open("/itemdata", "_blank");
                                            }}
                                        >
                                            <td className="px-3 py-2 hover:underline">{detail.recipe_item}</td>

                                            <td className="px-3 py-2">{detail.item_type}</td>

                                            <td className="px-3 py-2">
                                                {getDisplayQuantity(detail.direct_usage_recipe_per_unit)}{" "}
                                                {detail.new_uom || detail.uom || ""}
                                            </td>

                                            {/* Sub-recipe names stacked */}
                                            <td className="px-3 py-2">
                                                {detail.sub_recipes?.length > 0 ? (
                                                    detail.sub_recipes.map((sr, i) => (
                                                        <React.Fragment key={i}>
                                                            {sr.sub_recipe_name}
                                                            {i < detail.sub_recipes.length - 1 && <br />}
                                                        </React.Fragment>
                                                    ))
                                                ) : "Not used"}
                                            </td>

                                            {/* Sub-recipe quantities stacked */}
                                            <td className="px-3 py-2">
                                                {detail.sub_recipes?.length > 0 ? (
                                                    detail.sub_recipes.map((sr, i) => (
                                                        <React.Fragment key={i}>
                                                            {getDisplayQuantity(sr.calculated_quantity)}{" "}
                                                            {sr.new_uom || sr.uom || ""}
                                                            {i < detail.sub_recipes.length - 1 && <br />}
                                                        </React.Fragment>
                                                    ))
                                                ) : "0"}
                                            </td>

                                            <td className="px-3 py-2">{detail.total_sold}</td>

                                            <td className="px-3 py-2 font-medium">
                                                {getDisplayQuantity(detail.total_consumed)}{" "}
                                                {detail.new_uom || detail.uom || ""}
                                            </td>

                                            {/* Delete button */}
                                            <td
                                                className="px-3 py-2"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <button
                                                    onClick={() => setConfirmRow(detail)}
                                                    className="p-1.5 rounded-md text-red-500 hover:bg-red-50 transition"
                                                    title="Delete"
                                                >
                                                    <FiTrash2 size={15} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="py-8 text-gray-500">No data available</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Summary */}
                    <div className="mt-2.5 flex justify-end gap-2 text-sm font-semibold">
                        <span>
                            Total Consumed: {getDisplayQuantity(summary?.total_consumed)} {displayUom}
                        </span>

                        <span>
                            ( Used in {recipeCount} {recipeCount === 1 ? "recipe" : "recipes"})
                        </span>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div
                    onMouseDown={handleMouseDown}
                    className={`flex justify-between items-center border-t px-4 py-3 ${isDragging ? "cursor-grabbing" : "cursor-grab"} bg-gray-50`}
                >
                    <button
                        onClick={() => window.open(`/items?item_name=${encodeURIComponent(itemDetails?.item_name)}`, "_blank")}
                        className="rounded-lg bg-violet-100 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-200 hover:text-violet-800 transition"
                    >
                        View History
                    </button>

                    <div className="flex items-center justify-end gap-6">
                        {offset !== null && (
                            <button
                                onClick={() => setOffset(null)}
                                className="flex items-center gap-1.5 rounded-lg bg-blue-100 px-4 py-2 text-sm font-medium text-blue-700 hover:bg-blue-200 hover:text-blue-800 transition"
                            >
                                <TbArrowsMinimize size={16} />
                                Recentre
                            </button>
                        )}
                        <button
                            onClick={() => setShowModal(false)}
                            className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium transition hover:bg-gray-300"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Inline confirmation dialog ── */}
            {confirmRow && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-4 flex flex-col gap-4">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex-shrink-0 rounded-full bg-red-100 p-2 text-red-600">
                                <FiTrash2 size={18} />
                            </div>
                            <div>
                                <p className="font-semibold text-gray-800">Delete recipe item?</p>
                                <p className="text-sm text-gray-500">
                                    Remove&nbsp;
                                    <span className="font-medium text-gray-700">"{confirmRow.recipe_item}"</span>
                                    &nbsp;from&nbsp;
                                    <span className="font-medium text-gray-700">"{itemDetails?.item_name}"</span>.
                                    This will trigger a recalculation and cannot be undone.
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setConfirmRow(null)}
                                disabled={deleting}
                                className="rounded-lg px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 transition disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                                className="rounded-lg px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50"
                            >
                                {deleting ? "Deleting…" : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryItemDetailModal;